import React, { useEffect, useState, useRef } from 'react';
import { X, Plus, Minus, Gift, Diamond, Heart, Sparkles, Star } from 'lucide-react';
interface EnhancedSpinWheelProps {
  isOpen: boolean;
  onClose: () => void;
  onRewardClaimed: (reward: any) => void;
  userTokens: {
    loveTokens: number;
    desireGems: number;
  };
}
interface Reward {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  type: 'tokens' | 'gems' | 'skin' | 'tease' | 'nothing';
  value: number;
  skinId?: string;
  description: string;
  baseChance: number;
}
export const EnhancedSpinWheel: React.FC<EnhancedSpinWheelProps> = ({
  isOpen,
  onClose,
  onRewardClaimed,
  userTokens
}) => {
  const [tokenAmount, setTokenAmount] = useState(0);
  const [customAmount, setCustomAmount] = useState('');
  const [isSpinning, setIsSpinning] = useState(false);
  const [spinDegrees, setSpinDegrees] = useState(0);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [showReward, setShowReward] = useState(false);
  const [spinProbability, setSpinProbability] = useState<Record<string, number>>({});
  const [hostAnimation, setHostAnimation] = useState('idle');
  const wheelRef = useRef<HTMLDivElement>(null);
  const rewards: Reward[] = [{
    id: 'reward1',
    name: '50 Love Tokens',
    icon: <Heart fill="currentColor" size={20} data-id="element-6975" />,
    color: 'from-pink-500 to-pink-700',
    type: 'tokens',
    value: 50,
    description: 'Boost your Love Tokens balance!',
    baseChance: 15
  }, {
    id: 'reward2',
    name: '10 Desire Gems',
    icon: <Diamond fill="currentColor" size={20} data-id="element-6976" />,
    color: 'from-blue-500 to-blue-700',
    type: 'gems',
    value: 10,
    description: 'Premium currency for exclusive content!',
    baseChance: 5
  }, {
    id: 'reward3',
    name: 'Exclusive Skin',
    icon: <Sparkles size={20} data-id="element-6977" />,
    color: 'from-purple-500 to-purple-700',
    type: 'skin',
    value: 1,
    skinId: 'mascha-nurse',
    description: 'Unlock a premium angel skin!',
    baseChance: 2
  }, {
    id: 'reward4',
    name: '25 Love Tokens',
    icon: <Heart fill="currentColor" size={20} data-id="element-6978" />,
    color: 'from-pink-400 to-pink-600',
    type: 'tokens',
    value: 25,
    description: 'Add some tokens to your balance!',
    baseChance: 20
  }, {
    id: 'reward5',
    name: 'Tease Preview',
    icon: <Star fill="currentColor" size={20} data-id="element-6979" />,
    color: 'from-amber-500 to-amber-700',
    type: 'tease',
    value: 1,
    description: 'Unlock a special tease preview!',
    baseChance: 10
  }, {
    id: 'reward6',
    name: 'Better Luck Next Time',
    icon: <X size={20} data-id="element-6980" />,
    color: 'from-gray-500 to-gray-700',
    type: 'nothing',
    value: 0,
    description: 'No reward this time, try again!',
    baseChance: 48
  }];
  useEffect(() => {
    if (isOpen) {
      setTokenAmount(0);
      setCustomAmount('');
      setIsSpinning(false);
      setSelectedReward(null);
      setShowReward(false);
      setSpinDegrees(0);
      setHostAnimation('idle');
      calculateProbabilities(0);
    }
  }, [isOpen]);
  // Calculate probabilities based on token amount
  const calculateProbabilities = (tokens: number) => {
    // Boost factor based on tokens (diminishing returns)
    const boostFactor = tokens === 0 ? 0 : Math.log10(tokens + 1) * 2;
    // Calculate new probabilities
    const newProbabilities: Record<string, number> = {};
    let totalProbability = 0;
    rewards.forEach(reward => {
      if (reward.type === 'nothing') {
        // Reduce the "nothing" chance as tokens increase
        const reducedChance = Math.max(5, reward.baseChance - boostFactor * 4);
        newProbabilities[reward.id] = reducedChance;
        totalProbability += reducedChance;
      } else {
        // Increase other rewards proportionally
        const boost = reward.baseChance * (1 + boostFactor / 10);
        newProbabilities[reward.id] = boost;
        totalProbability += boost;
      }
    });
    // Normalize probabilities to sum to 100
    const normalizedProbabilities: Record<string, number> = {};
    rewards.forEach(reward => {
      normalizedProbabilities[reward.id] = newProbabilities[reward.id] / totalProbability * 100;
    });
    setSpinProbability(normalizedProbabilities);
  };
  const handleTokenAmountChange = (amount: number) => {
    if (amount < 0) return;
    if (amount > userTokens.loveTokens) return;
    setTokenAmount(amount);
    setCustomAmount(amount.toString());
    calculateProbabilities(amount);
  };
  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setCustomAmount('');
      setTokenAmount(0);
      calculateProbabilities(0);
      return;
    }
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;
    if (numValue <= userTokens.loveTokens) {
      setCustomAmount(value);
      setTokenAmount(numValue);
      calculateProbabilities(numValue);
    }
  };
  const handleSpin = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setShowReward(false);
    setHostAnimation('spinning');
    // Determine winning reward based on calculated probabilities
    const rand = Math.random() * 100;
    let cumulativeProbability = 0;
    let winningReward = rewards[rewards.length - 1]; // Default to "nothing" reward
    for (const reward of rewards) {
      cumulativeProbability += spinProbability[reward.id];
      if (rand <= cumulativeProbability) {
        winningReward = reward;
        break;
      }
    }
    // Calculate spin degrees
    const segmentDegrees = 360 / rewards.length;
    const winningIndex = rewards.findIndex(r => r.id === winningReward.id);
    const landingPosition = 360 - (winningIndex * segmentDegrees + segmentDegrees / 2);
    const totalDegrees = 1800 + landingPosition; // Multiple spins + landing position
    setSpinDegrees(totalDegrees);
    setSelectedReward(winningReward);
    // After spinning completes
    setTimeout(() => {
      setIsSpinning(false);
      setShowReward(true);
      setHostAnimation(winningReward.type === 'nothing' ? 'disappointed' : 'excited');
    }, 5000);
  };
  const handleClaimReward = () => {
    if (selectedReward) {
      onRewardClaimed({
        ...selectedReward,
        tokensCost: tokenAmount
      });
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-6981">
      <div className="relative w-full max-w-4xl bg-gradient-to-b from-purple-900/50 to-black rounded-xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 border border-pink-800/30" data-id="element-6982">
        <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors" onClick={onClose} data-id="element-6983">
          <X size={18} data-id="element-6984" />
        </button>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-6" data-id="element-6985">
          {/* Left column - Host */}
          <div className="hidden md:block md:col-span-2 relative" data-id="element-6986">
            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent z-10" data-id="element-6987"></div>
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXJFcbgwSeXsSaQYxjo4wA/2.jpg" alt="Wheel Hostess" className={`w-full h-full object-cover object-top rounded-lg ${hostAnimation === 'spinning' ? 'animate-subtle-bounce' : hostAnimation === 'excited' ? 'animate-pulse' : ''}`} data-id="element-6988" />
            <div className="absolute bottom-4 left-4 right-4 z-20" data-id="element-6989">
              <div className="bg-black/60 backdrop-blur-sm p-3 rounded-lg border border-pink-800/30" data-id="element-6990">
                <h3 className="text-pink-400 font-medium text-lg" data-id="element-6991">
                  Angel Destiny
                </h3>
                <p className="text-white text-sm" data-id="element-6992">
                  {hostAnimation === 'idle' && "Ready to spin? I'll help you win big prizes!"}
                  {hostAnimation === 'spinning' && 'Round and round it goes, where it stops, nobody knows!'}
                  {hostAnimation === 'excited' && "Congratulations! You've won an amazing prize!"}
                  {hostAnimation === 'disappointed' && 'Aww, not lucky this time. Add more tokens to improve your chances!'}
                </p>
              </div>
            </div>
          </div>
          {/* Right column - Wheel and controls */}
          <div className="md:col-span-3 flex flex-col" data-id="element-6993">
            {/* Header */}
            <div className="text-center mb-4" data-id="element-6994">
              <h2 className="text-2xl font-bold text-white mb-1" data-id="element-6995">
                Angel's Fortune Wheel
              </h2>
              <p className="text-pink-300 text-sm" data-id="element-6996">
                Spend more tokens to increase your chances of winning amazing
                prizes!
              </p>
            </div>
            {!showReward ? <>
                {/* Wheel section */}
                <div className="relative flex flex-col items-center justify-center mb-4" data-id="element-6997">
                  {/* Wheel marker */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20" data-id="element-6998">
                    <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-pink-600" data-id="element-6999"></div>
                  </div>
                  {/* Wheel */}
                  <div className="relative w-64 h-64 mx-auto" data-id="element-7000">
                    <div ref={wheelRef} className="w-full h-full rounded-full border-4 border-pink-800 transition-transform duration-5000 ease-out shadow-lg shadow-pink-500/20" style={{
                  transform: `rotate(${spinDegrees}deg)`
                }} data-id="element-7001">
                      {/* Wheel segments */}
                      {rewards.map((reward, index) => {
                    const segmentDegrees = 360 / rewards.length;
                    const rotation = index * segmentDegrees;
                    return <div key={reward.id} className={`absolute top-0 left-0 w-full h-full origin-center`} style={{
                      transform: `rotate(${rotation}deg)`,
                      clipPath: `polygon(50% 50%, 50% 0%, ${50 + 50 * Math.cos(segmentDegrees * Math.PI / 180)}% ${50 - 50 * Math.sin(segmentDegrees * Math.PI / 180)}%, 50% 50%)`,
                      background: `linear-gradient(to right, ${reward.color.split(' ')[0].replace('from-', '')}, ${reward.color.split(' ')[1].replace('to-', '')})`
                    }} data-id="element-7002">
                            <div className="absolute text-white" style={{
                        top: '25%',
                        left: '50%',
                        transform: 'translateX(-50%) rotate(' + (180 - rotation) + 'deg)',
                        textShadow: '0 0 3px black'
                      }} data-id="element-7003">
                              {reward.icon}
                            </div>
                          </div>;
                  })}
                    </div>
                  </div>
                  {/* Token controls */}
                  <div className="w-full max-w-xs mt-6 bg-black/40 backdrop-blur-sm rounded-lg p-4 border border-pink-800/30" data-id="element-7004">
                    <div className="flex items-center justify-between mb-2" data-id="element-7005">
                      <span className="text-white" data-id="element-7006">Tokens to Spend:</span>
                      <span className="text-pink-400 font-bold" data-id="element-7007">
                        {tokenAmount} 💗
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-3" data-id="element-7008">
                      <button onClick={() => handleTokenAmountChange(Math.max(0, tokenAmount - 10))} className="w-8 h-8 flex items-center justify-center bg-gray-800 rounded-full text-white hover:bg-gray-700" disabled={tokenAmount <= 0} data-id="element-7009">
                        <Minus size={16} data-id="element-7010" />
                      </button>
                      <input type="text" value={customAmount} onChange={handleCustomAmountChange} className="flex-1 bg-gray-800 border border-gray-700 rounded-md px-3 py-1 text-white text-center" placeholder="Enter amount" data-id="element-7011" />
                      <button onClick={() => handleTokenAmountChange(tokenAmount + 10)} className="w-8 h-8 flex items-center justify-center bg-gray-800 rounded-full text-white hover:bg-gray-700" disabled={tokenAmount >= userTokens.loveTokens} data-id="element-7012">
                        <Plus size={16} data-id="element-7013" />
                      </button>
                    </div>
                    <div className="mb-3" data-id="element-7014">
                      <div className="flex items-center justify-between mb-1" data-id="element-7015">
                        <span className="text-xs text-gray-300" data-id="element-7016">
                          Win Chance Boost
                        </span>
                        <span className="text-xs text-pink-400" data-id="element-7017">
                          {tokenAmount === 0 ? '0%' : `+${Math.min(80, Math.round(Math.log10(tokenAmount + 1) * 20))}%`}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-800 rounded-full overflow-hidden" data-id="element-7018">
                        <div className="h-full bg-gradient-to-r from-pink-600 to-purple-600 transition-all duration-300" style={{
                      width: `${Math.min(100, Math.round(Math.log10(tokenAmount + 1) * 20))}%`
                    }} data-id="element-7019"></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400 mb-4" data-id="element-7020">
                      <span data-id="element-7021">Your Balance: {userTokens.loveTokens} 💗</span>
                      <button className="text-pink-400 hover:text-pink-300" onClick={() => handleTokenAmountChange(userTokens.loveTokens)} data-id="element-7022">
                        Max
                      </button>
                    </div>
                    <button onClick={handleSpin} disabled={isSpinning} className={`w-full py-3 rounded-lg text-white font-medium ${isSpinning ? 'bg-gray-700 cursor-not-allowed' : 'bg-gradient-to-r from-pink-600 to-purple-600 hover:shadow-lg hover:shadow-pink-500/20 animate-pulse hover:animate-none'} transition-all`} data-id="element-7023">
                      {isSpinning ? 'Spinning...' : tokenAmount > 0 ? `Spin for ${tokenAmount} Tokens` : 'Free Spin!'}
                    </button>
                  </div>
                </div>
                {/* Prize odds */}
                <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-7024">
                  {rewards.map(reward => <div key={reward.id} className="flex items-center gap-2 bg-black/30 p-2 rounded-md border border-gray-800" data-id="element-7025">
                      <div className={`w-6 h-6 rounded-full bg-gradient-to-r ${reward.color} flex items-center justify-center text-white`} data-id="element-7026">
                        {reward.icon}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-7027">
                        <div className="text-xs text-white truncate" data-id="element-7028">
                          {reward.name}
                        </div>
                      </div>
                      <div className="text-xs font-medium text-pink-400" data-id="element-7029">
                        {Math.round(spinProbability[reward.id] || 0)}%
                      </div>
                    </div>)}
                </div>
              </> :
          // Reward reveal
          <div className="text-center py-6 animate-in fade-in zoom-in duration-300" data-id="element-7030">
                <div className="w-24 h-24 mx-auto bg-gradient-to-r from-pink-600 to-purple-600 rounded-full flex items-center justify-center mb-4" data-id="element-7031">
                  <span className="text-4xl" data-id="element-7032">{selectedReward?.icon}</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-7033">
                  {selectedReward?.name}
                </h3>
                <p className="text-gray-300 mb-6" data-id="element-7034">
                  {selectedReward?.description}
                </p>
                <button onClick={handleClaimReward} className="px-8 py-3 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-pink-500/20 transition-all" data-id="element-7035">
                  {selectedReward?.type === 'nothing' ? 'Try Again' : 'Claim Reward'}
                </button>
              </div>}
          </div>
        </div>
      </div>
      <style jsx data-id="element-7036">{`
        @keyframes subtle-bounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        .animate-subtle-bounce {
          animation: subtle-bounce 1s infinite;
        }
        .duration-5000 {
          transition-duration: 5000ms;
        }
      `}</style>
    </div>;
};